import { useEffect, useState, useSyncExternalStore } from 'react';
import { getServerClock } from '../time/serverClock';
import { currentReachability, useReachability } from './useReachability';
import { Reachability } from './reachability';

/**
 * Por que uma ação que grava está bloqueada.
 *
 * - `OFFLINE`: a sonda confirmou que não há internet (#867).
 * - `CLOCK_PENDING`: ainda não há âncora de hora do servidor — gravar agora
 *   carimbaria o relógio do aparelho, que o web não usa (#1010).
 */
export const BlockReason = {
  OFFLINE: 'OFFLINE',
  CLOCK_PENDING: 'CLOCK_PENDING',
} as const;
export type BlockReason = (typeof BlockReason)[keyof typeof BlockReason];

/**
 * Erro tipado de ação recusada por falta de internet ou de hora do servidor.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Contrato: lançado por `assertCanWrite()`; quem captura mostra o aviso padrão
 * ("sem internet") em vez de "Erro inesperado". Usado por: wrappers de escrita
 * dos três web apps (`useLabelOps`, `lib/callables.ts`, `firestore*.ts`).
 */
export class RequiresInternetError extends Error {
  readonly reason: BlockReason;
  constructor(reason: BlockReason) {
    super(reason === BlockReason.OFFLINE ? 'requires_internet' : 'server_clock_pending');
    this.name = 'RequiresInternetError';
    this.reason = reason;
  }
}

/**
 * Motivo do bloqueio agora, ou `null` se pode gravar.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Usado por: `assertCanWrite`, `assertOnline` e testes.
 *
 * Contrato: `UNKNOWN` (antes da 1ª sonda) NÃO bloqueia — mesmo critério do
 * banner, para não travar o boot; só `OFFLINE` confirmado bloqueia. Sem clock
 * configurado (teste unitário) não bloqueia por hora.
 */
export function currentBlockReason(): BlockReason | null {
  if (currentReachability() === Reachability.OFFLINE) return BlockReason.OFFLINE;
  const clock = getServerClock();
  if (clock !== null && !clock.isReady()) return BlockReason.CLOCK_PENDING;
  return null;
}

/**
 * Porta única das escritas (Firestore, callable, Storage): lança
 * `RequiresInternetError` se offline ou sem hora do servidor.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Por quê: o web não tem fila offline (decisão do owner); uma escrita sem rede
 * ficaria pendurada no SDK sem retorno para o usuário, e sem âncora de hora
 * gravaria o relógio do aparelho. Recusar cedo dá resposta imediata.
 *
 * Usado por: `useLabelOps`, `firestoreProducts`, `firestoreSuppliers`,
 * `lib/callables.ts`, etc. (lista em `docs/web-online-clock-inventory.md`, cat. B).
 */
export function assertCanWrite(): void {
  const reason = currentBlockReason();
  if (reason !== null) throw new RequiresInternetError(reason);
}

/**
 * Só a rede: lança se OFFLINE confirmado, sem exigir âncora de hora.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Por quê: callables de LEITURA (plano, contato de suporte, link de pagamento
 * pré-login) não carimbam hora; bloquear por `CLOCK_PENDING` travaria a tela
 * de pagamento anônima por um relógio que ela não usa. Falhar já offline
 * dá a mensagem certa em vez de esperar o timeout do SDK.
 *
 * Usado por: o wrapper `callable()` de `apps/app/src/lib/callables.ts` (e os
 * equivalentes de admin/CRM).
 */
export function assertOnline(): void {
  if (currentReachability() === Reachability.OFFLINE) throw new RequiresInternetError(BlockReason.OFFLINE);
}

/**
 * Hook para a UI: `blocked` desabilita botões que gravam; `reason` escolhe o texto.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: formulários e ações dos
 * três web apps; `ConnectivityBanner.tsx`.
 */
export function useRequiresInternet(): { blocked: boolean; reason: BlockReason | null } {
  const reachability = useReachability();
  const clock = getServerClock();
  const ready = useSyncExternalStore(
    (cb): (() => void) => (clock === null ? (): void => undefined : clock.subscribe(cb)),
    () => clock === null || clock.isReady(),
    () => true,
  );
  if (reachability === Reachability.OFFLINE) return { blocked: true, reason: BlockReason.OFFLINE };
  if (!ready) return { blocked: true, reason: BlockReason.CLOCK_PENDING };
  return { blocked: false, reason: null };
}

/** Quanto a pendência de hora precisa durar para a FAIXA aparecer (o bloqueio de escrita é imediato). */
export const CLOCK_PENDING_NOTICE_DELAY_MS = 2_500;

/**
 * Motivo a MOSTRAR na faixa (ou `null`): igual a `useRequiresInternet().reason`,
 * mas `CLOCK_PENDING` só vira aviso depois de `CLOCK_PENDING_NOTICE_DELAY_MS`.
 *
 * Origem: #1010 — 2026-10-20 15:50 +0200 (auditoria global: a faixa piscava em TODO boot, porque
 * antes da 1ª âncora de hora — alguns décimos de segundo — o motivo já era
 * `CLOCK_PENDING`; comprovado por e2e).
 *
 * Por quê: bloquear gravação por alguns ms é invisível; um aviso amarelo que
 * pisca e desloca o layout a cada carga de página não é. `OFFLINE` confirmado
 * pela sonda continua imediato.
 *
 * Usado por: `ConnectivityBanner.tsx` e `useConnectivityFlashItem` (admin/CRM).
 */
export function useConnectivityNotice(): BlockReason | null {
  const { reason } = useRequiresInternet();
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (reason !== BlockReason.CLOCK_PENDING) {
      setLate(false);
      return undefined;
    }
    const id = setTimeout(() => {
      setLate(true);
    }, CLOCK_PENDING_NOTICE_DELAY_MS);
    return (): void => {
      clearTimeout(id);
    };
  }, [reason]);
  if (reason === BlockReason.CLOCK_PENDING) return late ? reason : null;
  return reason;
}
